import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import DashboardApi from "../../Api/DashboardApi";

const formatDateDisplay = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-").map(Number);
    if (parts.length === 3) {
        const d = new Date(parts[0], parts[1] - 1, parts[2]);
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    }
    return dateStr;
};

const SalesOverviewChart = ({ filter, date }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState({
        series: [{ name: 'Total Sales', data: [] }],
        categories: []
    });

    useEffect(() => {
        fetchSalesData();
    }, [filter, date]);

    const fetchSalesData = async () => {
        setLoading(true);
        try {
            const res = await DashboardApi.getSalesOverview(filter, date);
            if (res.status && res.response.data) {
                processChartData(res.response.data);
            } else {
                setChartData({ series: [{ name: 'Total Sales', data: [] }], categories: [] });
            }
        } catch (error) {
            console.error("Error fetching sales overview:", error);
        } finally {
            setLoading(false);
        }
    };

    const processChartData = (data) => {
        let categories = [];
        let salesData = [];

        if (filter === "Day") {
            categories = Array.from({ length: 24 }, (_, i) => `${i}:00`);
            salesData = new Array(24).fill(0);
            data.forEach(item => {
                const hour = item._id.hour;
                salesData[hour] = item.totalAmount;
            });
        } else if (filter === "Weekly") {
            const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
            categories = days;
            salesData = new Array(7).fill(0);
            data.forEach(item => {
                const dayIdx = item._id.dayOfWeek - 1;
                salesData[dayIdx] = item.totalAmount;
            });
        } else if (filter === "Monthly") {
            const uniqueWeeks = [...new Set(data.map(d => d._id.week))].sort((a, b) => a - b);
            categories = uniqueWeeks.length > 0 ? uniqueWeeks.map((w, i) => `Week ${i + 1}`) : ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5"];
            salesData = new Array(categories.length).fill(0);
            data.forEach(item => {
                const weekIdx = uniqueWeeks.indexOf(item._id.week);
                if (weekIdx !== -1) salesData[weekIdx] = item.totalAmount;
            });
        } else if (filter === "Yearly") {
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            categories = months;
            salesData = new Array(12).fill(0);
            data.forEach(item => {
                const monthIdx = item._id.month - 1;
                salesData[monthIdx] = item.totalAmount;
            });
        }

        setChartData({
            series: [{ name: 'Total Sales', data: salesData }],
            categories: categories
        });
    };

    const options = {
        chart: {
            type: 'bar',
            height: 350,
            toolbar: { show: false }
        },
        grid: {
            padding: {
                left: 20,
                right: 20
            }
        },
        noData: {
            text: 'No Data Available',
            align: 'center',
            verticalAlign: 'middle',
            style: {
                color: '#8e8e8e',
                fontSize: '14px'
            }
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '40%',
                borderRadius: 4
            },
        },
        dataLabels: { enabled: false },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        xaxis: {
            categories: chartData.categories,
            title: { text: null }
        },
        yaxis: {
            title: {
                text: 'Sales Amount (₹)',
                rotate: -90,
                offsetX: 0,
                style: {
                    color: '#475467',
                    fontSize: '12px',
                    fontWeight: 600
                }
            },
            labels: {
                minWidth: 85,
                maxWidth: 120,
                formatter: (val) => `₹${val.toLocaleString()}`
            }
        },
        fill: {
            opacity: 1,
            colors: ['#364e3d']
        },
        colors: ['#364e3d'],
        tooltip: {
            y: {
                formatter: (val) => `₹${val.toLocaleString()}`
            }
        }
    };

    return (
        <div className="card radius-12 border-0 shadow-sm mt-4">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-lg fw-semibold mb-0">
                    Sales Overview - {filter === "Day" && date ? `Day (${formatDateDisplay(date)})` : filter}
                </h6>
            </div>
            <div className="card-body p-24">
                {loading ? (
                    <div className="skeleton-graph w-100 bg-light radius-8" style={{ height: '350px' }}>
                        <div className="d-flex align-items-center justify-content-center h-100">
                            <div className="spinner-border text-primary" role="status"></div>
                            <span className="ms-2">Loading chart...</span>
                        </div>
                    </div>
                ) : chartData.series[0].data.every(val => val === 0) ? (
                    <div className="d-flex flex-column align-items-center justify-content-center py-50 bg-light radius-8" style={{ height: '350px' }}>
                        <p className="text-secondary-light" style={{ fontSize: '16px' }}>No sales data found for this period</p>
                    </div>
                ) : (
                    <ReactApexChart
                        options={options}
                        series={chartData.series}
                        type="bar"
                        height={350}
                    />
                )}
            </div>
        </div>
    );
};


export default SalesOverviewChart;

